import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import api from '../services/api';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

const ModalNovoAgendamento = ({ show, onHide, onAgendamentoCriado }) => {
  const [especialidades, setEspecialidades] = useState([]);
  const [campi, setCampi] = useState([]);
  const [especialidade, setEspecialidade] = useState('');
  const [campus, setCampus] = useState('');
  const [dataReferencia, setDataReferencia] = useState('');
  const [slotsPorData, setSlotsPorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erroGeral, setErroGeral] = useState('');
  const [errosPorSlot, setErrosPorSlot] = useState({});

  useEffect(() => {
    if (show) {
      carregarEspecialidades();
      carregarCampi();
      resetarCampos();
    }
  }, [show]);

  const resetarCampos = () => {
    setEspecialidade('');
    setCampus('');
    setDataReferencia('');
    setSlotsPorData([]);
    setErroGeral('');
    setErrosPorSlot({});
  };

  const carregarEspecialidades = async () => {
    try {
      const response = await api.get('especialidades/');
      setEspecialidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar especialidades', error);
    }
  };

  const carregarCampi = async () => {
    try {
      const response = await api.get('campus/');
      setCampi(response.data);
    } catch (error) {
      console.error('Erro ao carregar campi', error);
    }
  };

  const buscarSlots = async () => {
    if (!especialidade || !campus || !dataReferencia) {
      setErroGeral('Selecione especialidade, campus e data.');
      return;
    }

    setErroGeral('');
    setErrosPorSlot({});
    setLoading(true);
    setSlotsPorData([]);

    try {
      const response = await api.get('agendamentos/slots-multidatas/', {
        params: {
          especialidade,
          campus,
          data_referencia: dataReferencia,
          direcao: 'proximas',
        },
      });
      setSlotsPorData(response.data);
    } catch (error) {
      console.error('Erro ao buscar slots:', error);
      setErroGeral('Erro ao buscar disponibilidade.');
    } finally {
      setLoading(false);
    }
  };

  const confirmarAgendamento = async (slot) => {
    const chave = `${slot.data}-${slot.hora_inicio}-${slot.espaco.id}`;
    try {
      await api.post('agendamentos/', {
        data: slot.data,
        hora_inicio: slot.hora_inicio,
        espaco: slot.espaco.id,
        especialidade: slot.especialidade.id,
        campus: campus,
      });
      onAgendamentoCriado();
      onHide();
    } catch (error) {
      console.error('Erro ao agendar:', error);
      const resposta = error.response?.data;
      let mensagem = 'Erro ao confirmar agendamento.';

      const nonFieldErrors = resposta?.non_field_errors || resposta?.__all__;
      if (Array.isArray(nonFieldErrors)) {
        if (nonFieldErrors.some(msg =>
          msg.includes('paciente já possui') || msg.includes('mesmo horário')
        )) {
          mensagem = 'Você já tem um agendamento nesse mesmo horário.';
        } else {
          mensagem = nonFieldErrors[0];
        }
      }

      setErrosPorSlot((prev) => ({
        ...prev,
        [chave]: mensagem,
      }));
    }
  };

  const formatarDataLegivel = (data) => {
    return format(parseISO(data), "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Novo Agendamento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row mb-3">
            <div className="col-md-4">
              <Form.Label>Especialidade</Form.Label>
              <Form.Select
                value={especialidade}
                onChange={(e) => setEspecialidade(e.target.value)}
              >
                <option value="">Selecione</option>
                {especialidades.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>Campus</Form.Label>
              <Form.Select
                value={campus}
                onChange={(e) => setCampus(e.target.value)}
              >
                <option value="">Selecione</option>
                {campi.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>Data de Referência</Form.Label>
              <Form.Control
                type="date"
                value={dataReferencia}
                onChange={(e) => setDataReferencia(e.target.value)}
              />
            </div>
          </div>

          <Button variant="secondary" onClick={buscarSlots} disabled={loading}>
            {loading ? 'Buscando...' : 'Pesquisar Disponibilidade'}
          </Button>

          {erroGeral && <Alert variant="danger" className="mt-3">{erroGeral}</Alert>}
        </Form>

        <hr />

        {slotsPorData.length > 0 && (
          <div>
            {slotsPorData.map((dia, idx) => (
              <div key={idx} className="mb-4">
                <h5 className="mb-2">{formatarDataLegivel(dia.data)}</h5>
                {dia.slots.length > 0 ? (
                  dia.slots.map((slot, i) => {
                    const chave = `${dia.data}-${slot.hora_inicio}-${slot.espaco.id}`;
                    return (
                      <div
                        key={i}
                        className="d-flex flex-column border rounded p-2 mb-2"
                      >
                        {errosPorSlot[chave] && (
                          <Alert variant="danger" className="mb-2 w-100">
                            {errosPorSlot[chave]}
                          </Alert>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                          <span>
                            {slot.hora_inicio} - {slot.hora_fim} |{' '}
                            {slot.espaco.nome} | {slot.vagas_disponiveis} vagas restantes
                          </span>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={Number(slot.vagas_disponiveis) <= 0}
                            onClick={() =>
                              confirmarAgendamento({
                                ...slot,
                                data: dia.data,
                              })
                            }
                          >
                            {Number(slot.vagas_disponiveis) <= 0 ? 'Esgotado' : 'Agendar'}
                          </Button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted">Nenhum horário disponível neste dia para consulta.</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalNovoAgendamento;